import { useEffect, useRef, useState } from 'react'
import MapView from './components/MapView'
import PlannerPanel from './components/PlannerPanel'
import ToursPanel from './components/ToursPanel'
import DiscoverPanel from './components/DiscoverPanel'
import ProfilePanel from './components/ProfilePanel'
import RecordBar from './components/RecordBar'
import NavOverlay from './components/NavOverlay'
import { useUi, type Panel } from './state/ui'
import { useRecorder } from './state/recorder'
import { useNav } from './state/nav'
import { usePlanner, plannerStats } from './state/planner'
import { SPORTS, type Sport } from './lib/models'
import { formatDuration, formatKm } from './lib/geo'
import { friendlyGeoError, geoPermissionDenied } from './lib/platform'
import {
  IconPlan,
  IconTours,
  IconDiscover,
  IconProfile,
  IconLayers,
  IconRecord,
  IconHike,
  IconBike,
  IconMtb,
  IconRoad,
  IconGravel,
  IconRun,
  IconClose,
  IconNavArrow,
} from './components/icons'

const TABS: { id: Panel; label: string; Icon: typeof IconPlan }[] = [
  { id: 'plan', label: 'Plan', Icon: IconPlan },
  { id: 'tours', label: 'Tours', Icon: IconTours },
  { id: 'discover', label: 'Discover', Icon: IconDiscover },
  { id: 'profile', label: 'Profile', Icon: IconProfile },
]

export const SPORT_ICONS: Record<Sport, typeof IconHike> = {
  hike: IconHike,
  bike: IconBike,
  mtb: IconMtb,
  road: IconRoad,
  gravel: IconGravel,
  run: IconRun,
}

export default function App() {
  const ui = useUi()
  const recStatus = useRecorder((s) => s.status)
  const navActive = useNav((s) => s.active)
  const waypointCount = usePlanner((s) => s.waypoints.length)
  const [layerMenu, setLayerMenu] = useState(false)
  const [sportPick, setSportPick] = useState(false)

  const immersive = navActive || recStatus !== 'idle'

  return (
    <div className="app">
      <MapView />

      {!immersive && (
        <>
          <Sheet />
          <div className="map-fabs">
            <button className="fab" title="Map layers & 3D" onClick={() => setLayerMenu(!layerMenu)}>
              <IconLayers />
            </button>
            <button className="fab record" title="Record an activity" onClick={() => setSportPick(true)}>
              <IconRecord />
            </button>
          </div>
          {layerMenu && <LayerPopover onClose={() => setLayerMenu(false)} />}
          {sportPick && <SportChooser onClose={() => setSportPick(false)} />}
          {ui.panel === 'plan' &&
            waypointCount === 0 &&
            (!ui.sheetOpen || window.matchMedia('(min-width: 900px)').matches) && (
              <div className="coach">
                <span className="c-dot" />
                Tap the map to start your route
              </div>
            )}
        </>
      )}

      <RecordBar />
      <NavOverlay />
      {ui.toast && <div className="toast">{ui.toast}</div>}
    </div>
  )
}

function SportChooser({ onClose }: { onClose: () => void }) {
  const [blocked, setBlocked] = useState(false)

  useEffect(() => {
    void geoPermissionDenied().then(setBlocked)
  }, [])

  return (
    <div className="modal-veil" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row">
          <h3 className="m-title">What are you up to?</h3>
          <span className="spacer" />
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>
        {blocked && <div className="hint" style={{ marginTop: 0 }}>{friendlyGeoError(1)}</div>}
        <div className="sports">
          {SPORTS.map((s) => {
            const Icon = SPORT_ICONS[s.id]
            return (
              <button
                key={s.id}
                onClick={() => {
                  onClose()
                  useRecorder.getState().start(s.id)
                }}
              >
                <Icon />
                {s.label}
              </button>
            )
          })}
        </div>
        <p className="muted" style={{ margin: '4px 2px 0' }}>
          Recording starts immediately — keep the screen on while you move.
        </p>
      </div>
    </div>
  )
}

function Sheet() {
  const ui = useUi()
  const panelRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ startY: number; wasOpen: boolean; moved: boolean } | null>(null)

  const onGripPointerDown = (e: React.PointerEvent) => {
    drag.current = { startY: e.clientY, wasOpen: ui.sheetOpen, moved: false }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onGripPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !panelRef.current) return
    const dy = e.clientY - drag.current.startY
    if (Math.abs(dy) > 4) drag.current.moved = true
    panelRef.current.classList.add('dragging')
    const base = drag.current.wasOpen ? 0 : panelRef.current.getBoundingClientRect().height - 116
    panelRef.current.style.transform = `translateY(${Math.max(0, base + dy)}px)`
  }
  const onGripPointerUp = (e: React.PointerEvent) => {
    if (!drag.current || !panelRef.current) return
    const dy = e.clientY - drag.current.startY
    panelRef.current.classList.remove('dragging')
    panelRef.current.style.transform = ''
    if (drag.current.moved) ui.setSheetOpen(dy < -30 ? true : dy > 30 ? false : drag.current.wasOpen)
    else ui.setSheetOpen(!ui.sheetOpen)
    drag.current = null
  }

  return (
    <>
      <div ref={panelRef} className={`panel${ui.sheetOpen ? '' : ' collapsed'}`}>
        <div className="brand">
          <span className="b-dot">
            <IconNavArrow width={15} height={15} />
          </span>
          <div>
            <div className="b-name">Traverse</div>
          </div>
          <span className="spacer" />
          <span className="b-tag">Plan · Ride · Hike</span>
        </div>
        <DesktopTabs />
        <div
          className="sheet-grip"
          onPointerDown={onGripPointerDown}
          onPointerMove={onGripPointerMove}
          onPointerUp={onGripPointerUp}
          aria-label="Drag to resize panel"
        />
        <PeekRow />
        <div className="panel-body" key={ui.panel}>
          {ui.panel === 'plan' && <PlannerPanel />}
          {ui.panel === 'tours' && <ToursPanel />}
          {ui.panel === 'discover' && <DiscoverPanel />}
          {ui.panel === 'profile' && <ProfilePanel />}
        </div>
      </div>
      <MobileTabs />
    </>
  )
}

/** Compact stats visible when the mobile sheet is collapsed. */
function PeekRow() {
  const ui = useUi()
  const p = usePlanner()
  const stats = plannerStats(p)

  if (ui.panel !== 'plan' || !stats) {
    const tab = TABS.find((t) => t.id === ui.panel)
    return (
      <div className="peek">
        <div className="p-main">
          <div className="p-stats">{tab?.label}</div>
          <div className="p-sub">
            {ui.panel === 'plan' ? 'Tap the map or search to start' : 'Swipe up for details'}
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="peek">
      <div className="p-main">
        <div className="p-stats">
          {formatKm(stats.distanceM)} · {formatDuration(stats.durationS)} · ↗ {stats.ascentM} m
        </div>
        <div className="p-sub">
          {SPORTS.find((s) => s.id === p.sport)?.label} · swipe up to edit & save
        </div>
      </div>
      <span className={`difficulty ${stats.difficulty}`}>{stats.difficulty}</span>
    </div>
  )
}

function DesktopTabs() {
  const { panel, setPanel } = useUi()
  return (
    <div className="tabbar tabbar-desktop">
      {TABS.map(({ id, label, Icon }) => (
        <button key={id} className={panel === id ? 'active' : ''} onClick={() => setPanel(id)}>
          <Icon />
          <span className="tab-label">{label}</span>
        </button>
      ))}
    </div>
  )
}

function MobileTabs() {
  const { panel, setPanel, sheetOpen, setSheetOpen } = useUi()
  return (
    <div className="tabbar tabbar-mobile">
      {TABS.map(({ id, label, Icon }) => (
        <button
          key={id}
          className={panel === id ? 'active' : ''}
          onClick={() => {
            if (panel === id) setSheetOpen(!sheetOpen)
            else setPanel(id)
          }}
        >
          <Icon />
          {label}
        </button>
      ))}
    </div>
  )
}

function LayerPopover({ onClose }: { onClose: () => void }) {
  const ui = useUi()
  return (
    <div className="layer-pop">
      <div className="lp-title">Map style</div>
      <div className="lp-grid">
        {(
          [
            ['outdoor', 'Outdoor'],
            ['topo', 'Topo'],
            ['satellite', 'Satellite'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            className={`lp-item${ui.layer === id ? ' active' : ''}`}
            onClick={() => {
              ui.setLayer(id)
              onClose()
            }}
          >
            <span className={`lp-thumb ${id}`} />
            {label}
          </button>
        ))}
      </div>
      <button className={`lp-3d${ui.terrain3d ? ' on' : ''}`} onClick={() => ui.setTerrain3d(!ui.terrain3d)}>
        3D terrain
        <span className="sw" />
      </button>
    </div>
  )
}
